import { Card, CardContent, CardHeader } from "../ui/card";
import { ScrollArea } from "../ui/scroll-area";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Filter, MessageSquare } from "lucide-react";
import type { ChatHistoryOrder } from "@/lib/session-time";
import { SessionFilters } from "./SessionFilters";
import { SessionSelector } from "./SessionSelector";
import { MessageList } from "./MessageList";
import type { UserOption, ChatHistory, Session, Lesson } from "./types";

interface ChatPanelProps {
  selectedUser: UserOption | undefined;
  chatHistory: ChatHistory | null;
  activeSessionIndex: number;
  isLoadingChat: boolean;
  isLoadingMore: boolean;
  isFiltersExpanded: boolean;
  hasActiveFilters: boolean;
  selectedLesson: Lesson | null;
  startDate: string;
  endDate: string;
  nextToken: string | null;
  onFiltersExpandedChange: (expanded: boolean) => void;
  onSessionChange: (index: number) => void;
  onLoadMoreSessions: () => void;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onLessonChange: (lesson: Lesson | null) => void;
  onApplyFilters: () => void;
  onClearFilters: () => void;
  /** The order asked for. */
  orderBy: ChatHistoryOrder;
  /** The order the list is in; "started" when the studio could not order by save time yet. */
  listedOrder: ChatHistoryOrder;
  onOrderByChange: (order: ChatHistoryOrder) => void;
  getSessionLabel: (session: Session, index: number) => string;
  formatMessageTime: (session: Session) => string;
}

export const ChatPanel = ({
  selectedUser,
  chatHistory,
  activeSessionIndex,
  isLoadingChat,
  isLoadingMore,
  isFiltersExpanded,
  hasActiveFilters,
  selectedLesson,
  startDate,
  endDate,
  nextToken,
  onFiltersExpandedChange,
  onSessionChange,
  onLoadMoreSessions,
  onStartDateChange,
  onEndDateChange,
  onLessonChange,
  onApplyFilters,
  onClearFilters,
  orderBy,
  listedOrder,
  onOrderByChange,
  getSessionLabel,
  formatMessageTime,
}: ChatPanelProps) => {
  const activeSession: Session | undefined =
    chatHistory?.sessions[activeSessionIndex];

  if (!selectedUser) {
    return (
      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden border border-border/60 bg-white/80 shadow-sm">
        <div className="flex h-full flex-col items-center justify-center p-6 text-center">
          <MessageSquare className="h-10 w-10 text-muted-foreground/50" />
          <h3 className="mt-3 text-sm font-medium">Select a User</h3>
          <p className="mt-1 text-xs text-muted-foreground max-w-sm">
            Choose a user from the list to view their conversation history.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="flex min-h-0 flex-1 flex-col overflow-hidden border border-border/60 bg-white/80 shadow-sm">
      {/* Chat Header */}
      <CardHeader className="border-b px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Avatar className="size-8">
              <AvatarFallback className="bg-muted text-xs font-medium">
                {selectedUser.name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <h2 className="text-sm font-medium">{selectedUser.name}</h2>
              <p className="text-xs text-muted-foreground capitalize">
                {selectedUser.type} • {chatHistory?.sessions.length || 0}
                {nextToken ? "+" : ""} sessions
              </p>
            </div>
          </div>

          {/* Filters Toggle */}
          <Button
            variant={isFiltersExpanded ? "secondary" : "outline"}
            size="sm"
            onClick={() => onFiltersExpandedChange(!isFiltersExpanded)}
          >
            <Filter className="h-4 w-4 mr-1.5" />
            Filters
            {hasActiveFilters && (
              <Badge className="ml-2 h-5 px-1.5" variant="secondary">
                {(selectedLesson ? 1 : 0) + (startDate || endDate ? 1 : 0)}
              </Badge>
            )}
          </Button>
        </div>

        {/* While a list loads, listedOrder still describes the old one. */}
        {listedOrder !== orderBy && !isLoadingChat && (
          <p role="status" className="mt-2 text-xs text-amber-800">
            Ordered and filtered by session start: ordering by last saved is
            not available yet.
          </p>
        )}

        {/* Filter Panel */}
        {isFiltersExpanded && (
          <SessionFilters
            startDate={startDate}
            endDate={endDate}
            selectedLesson={selectedLesson}
            userId={selectedUser.id}
            hasActiveFilters={hasActiveFilters}
            orderBy={orderBy}
            isOrderLocked={isLoadingChat || isLoadingMore}
            onOrderByChange={onOrderByChange}
            onStartDateChange={onStartDateChange}
            onEndDateChange={onEndDateChange}
            onLessonChange={onLessonChange}
            onApplyFilters={onApplyFilters}
            onClearFilters={onClearFilters}
          />
        )}

        {/* Session Selector */}
        {/* An empty page can still have more: the studio stops reading at a
            page cap before any row matched the lesson filter. */}
        {chatHistory && (chatHistory.sessions.length > 0 || nextToken) && (
          <SessionSelector
            sessions={chatHistory.sessions}
            activeSessionIndex={activeSessionIndex}
            isLoadingMore={isLoadingMore}
            hasMoreSessions={!!nextToken}
            onSessionChange={onSessionChange}
            onLoadMore={onLoadMoreSessions}
            getSessionLabel={getSessionLabel}
          />
        )}
      </CardHeader>

      {/* Messages Area */}
      <ScrollArea className="flex-1 min-h-0">
        <CardContent className="p-4">
          <MessageList
            session={activeSession}
            isLoading={isLoadingChat}
            formatMessageTime={formatMessageTime}
            userId={selectedUser.id}
          />
        </CardContent>
      </ScrollArea>
    </Card>
  );
};
